import { useState } from "react"
import { toast } from "sonner"
import {
    FiCheck,
    FiCopy,
    FiCornerUpRight,
    FiImage,
    FiLink,
    FiMoreVertical,
    FiSettings,
    FiDownload,
    FiTrash,
    FiPlay,
    FiMessageCircle,
} from "react-icons/fi"
import { FaPlay, FaCommentAlt, FaStarOfLife, FaVideo } from "react-icons/fa"
import { GrView } from "react-icons/gr";
import { LuBrainCircuit, LuLoaderCircle, LuAudioLines, LuImage } from "react-icons/lu"
import { MdDriveFileMove } from "react-icons/md"
import { IoIosRefresh } from "react-icons/io"

import AssetThumbnail from "../AssetThumbnail"
import AsyncEditableTextArea from "../ui/AsyncEditableTextArea"
import { useShow } from "@/contexts/ShowContext"
import { useDragPreview } from "../ui/DragPreview"
import { ASSET_DRAG_MIME, FOLDER_DRAG_MIME, assetMediaDragType } from "./constants"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuPortal
} from "../ui/shadcn/dropdown-menu"
import { GoTag } from "react-icons/go";
import { TagsEditor } from "@/components/TagsEditor"

// Single asset card. Handles its own drag (carrying the current selection if
// applicable) and an in-card hover toolbar for the per-asset actions.
export default function AssetItem({
    asset,
    onEdit,
    onDelete,
    onDownload,
    onPreview,
    onRefreshAsset,
    onAgent,
    onSketchEdit,
    onMove,
    onCopy,
    onRecallSettings,
    onInlineRename,
    onGoToFolder,
    showGoToFolder = false,
    selectable = false,
    selected = false,
    previewed = false,
    onToggleSelect,
    selectedIds = [],
    busy = false,
    selectedFolderIds = [],
    selectedAssetMediaTypes = [],
    onBeginFolderDrag,
    onItemDragStart,
}) {
    const [loading, setLoading] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [editingName, setEditingName] = useState(false)
    const [savingName, setSavingName] = useState(false)
    const [nameError, setNameError] = useState(null)
    const { show, tagSavingAssetIds } = useShow()
    const { showDragPreview } = useDragPreview()
    const showLoader = loading || busy || savingName || tagSavingAssetIds.has(asset._id)

    const handleRenameSave = async (name) => {
        setSavingName(true)
        setNameError(null)
        try {
            if (onInlineRename) await onInlineRename(asset, name)
        } catch (error) {
            console.error('Error renaming asset:', error)
            setNameError('Failed to rename')
        } finally {
            setSavingName(false)
        }
    }

    const handleOnEdit = async () => {
        setLoading(true)
        if (onEdit) await onEdit(asset)
        setLoading(false)
    }

    const handleOnDelete = async () => {
        setLoading(true)
        if (onDelete) await onDelete(asset)
        setLoading(false)
    }

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(asset.url)
            toast('Asset URL copied to clipboard')
        } catch {
            toast.error('Failed to copy asset URL')
        }
    }

    const handleDragStart = (e) => {
        const assetIds = selected && selectedIds.length > 0 ? selectedIds : [asset._id]
        const folderIds = selected ? selectedFolderIds : []
        const totalCount = assetIds.length + folderIds.length

        showDragPreview(e, {
            thumbnailUrl: asset.thumbnail_url || (asset.mediaType === 'image' ? asset.url : null),
            name: asset.name,
            mediaType: asset.mediaType,
            count: totalCount,
        })

        try {
            e.dataTransfer.setData(ASSET_DRAG_MIME, JSON.stringify(assetIds))
            if (folderIds.length > 0) {
                e.dataTransfer.setData(FOLDER_DRAG_MIME, JSON.stringify(folderIds))
            }
            // Per-mediaType marker(s) so drop targets can decide accept/reject
            // during dragover (when only `types` are readable, not values).
            // For multi-asset drags we set one marker per unique mediaType in
            // the selection so type-restricted pickers can accept a drag that
            // contains *some* matching asset (and filter the rest at drop).
            const markerTypes = new Set()
            if (asset.mediaType) markerTypes.add(asset.mediaType)
            if (selected && assetIds.length > 1) {
                for (const mt of selectedAssetMediaTypes) {
                    if (mt) markerTypes.add(mt)
                }
            }
            for (const mt of markerTypes) {
                e.dataTransfer.setData(assetMediaDragType(mt), '')
            }
        } catch {}
        // Allow both move (folder drops) and copy (asset-picker drops). Folder
        // and breadcrumb handlers still set their own dropEffect so the cursor
        // stays correct in those targets.
        e.dataTransfer.effectAllowed = 'copyMove'
        if (folderIds.length > 0) onBeginFolderDrag?.(folderIds)
        onItemDragStart?.({ assetIds, folderIds })
    }

    const getMediaIcon = () =>{
        if(asset.mediaType === 'audio') return <LuAudioLines className='w-3 h-3 text-secondary-foreground' />
        if(asset.mediaType === 'video') return <FaVideo className='w-3 h-3 text-secondary-foreground' />
        if(asset.mediaType === 'image') return <LuImage className='w-3 h-3 text-secondary-foreground' />
    }

    return (
        <div
            id={`${AssetItem.name}-${asset._id}`}
            className='flex flex-col gap-1 relative group @container'
            draggable={!editingName}
            onDragStart={handleDragStart}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={`flex justify-center items-center w-full relative aspect-video group/thumbnail bg-card rounded-md overflow-hidden border border-secondary/60 ${(selected || previewed) ? 'ring-2 ring-primary' : (selectable ? 'outline-2 outline-dashed outline-secondary-foreground/40' : '')}`}>
                <div onClick={() => selectable ? onToggleSelect?.(asset) : onPreview(asset)} className='cursor-pointer w-full h-full'>
                    <AssetThumbnail asset={asset} />
                </div>

                {selectable && selected && (
                    <div className='absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5'>
                        <FiCheck className='w-3 h-3' />
                    </div>
                )}

                <div 
                    id={`${AssetItem.name}Tags-${asset._id}`} 
                    className="absolute top-0 left-0 flex flex-wrap gap-1 p-1 pointer-events-none">
                    {(asset.tags || []).map(tag => (
                        <span key={tag} className='text-[9px] @[200px]:text-[11px] py-[1px] px-1 rounded-sm capitalize bg-primary text-primary-foreground'>
                            {show.asset_categories.find(t => t._id === tag)?.name}
                        </span>
                    ))}
                </div>

                {!selectable && <div className='absolute top-0 left-0 flex w-full h-full items-center justify-center pointer-events-none bg-background/60 hidden group-hover/thumbnail:flex'>
                    {asset.mediaType === 'image' ? 
                        <GrView className='w-1/4 h-1/4 max-w-8 max-h-8 text-secondary-foreground' /> 
                        :
                        <FaPlay className='w-1/4 h-1/4 max-w-8 max-h-8 text-secondary-foreground' />
                    }
                    
                </div>}

                <div className="absolute bottom-0 left-0 flex p-1 pointer-events-none ">
                    <div className='p-1.5 flex items-center justify-center bg-card rounded-md border text-secondary-foreground/80 relative overflow-hidden'>
                        {getMediaIcon()}
                        {asset.source==='generation' && 
                            <div className='absolute top-0 left-0 bg-card rounded-full flex items-center justify-center w-2.5 h-2.5'>
                                <FaStarOfLife className='w-1.5 h-1.5 text-secondary-foreground' />
                            </div>
                        }
                    </div>
                </div>

                {asset.commentCount > 0 && <div className="absolute top-0 right-0 flex p-1 pointer-events-none ">
                    <FaCommentAlt className='w-5 h-5 text-primary' />
                    <span className='absolute top-1 left-0 w-full flex items-center justify-center text-xs text-primary-foreground'>{asset.commentCount}</span>
                </div>}

               {!selectable && <div className='absolute bottom-0 right-0 p-1 hidden group-hover:flex data-[menu-open=true]:flex' data-menu-open={menuOpen}>
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type='button'
                                title='Actions'
                                className='p-1.5 flex items-center justify-center bg-card rounded-md border text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer'
                            >
                                <FiMoreVertical className='w-3.5 h-3.5' />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end' side='bottom'>
                            <DropdownMenuItem className='text-xs p-2' onClick={handleOnEdit}>
                                <FiSettings className='w-4 h-4' />
                                Settings
                            </DropdownMenuItem>
                            {asset.url && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onDownload(asset)}>
                                    <FiDownload className='w-4 h-4' />
                                    Download
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger className='text-xs p-2'>
                                    <GoTag className='w-4 h-4 mr-2' />
                                    Tags
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuSubContent className='w-64 p-2'>
                                        <TagsEditor
                                            assetCategories={show.asset_categories || []}
                                            assets={[asset]}
                                            onSaved={() => { onRefreshAsset?.(asset._id); setMenuOpen(false); }}
                                            onCancel={() => setMenuOpen(false)}
                                        />
                                    </DropdownMenuSubContent>
                                </DropdownMenuPortal>
                            </DropdownMenuSub>
                            {asset.url && (
                                <DropdownMenuItem className='text-xs p-2' onClick={handleCopyUrl}>
                                    <FiLink className='w-4 h-4' />
                                    Copy Asset URL
                                </DropdownMenuItem>
                            )}
                             <DropdownMenuSeparator />
                            {showGoToFolder && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onGoToFolder?.(asset)}>
                                    <FiCornerUpRight className='w-3 h-3' />
                                    Go to folder
                                </DropdownMenuItem>
                            )}
                            {asset.mediaType === 'image' && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onSketchEdit(asset)}>
                                    <FiImage className='w-3 h-3' />
                                    Edit
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className='text-xs p-2' onClick={() => onMove?.(asset)}>
                                <MdDriveFileMove className='w-4 h-4' />
                                Move
                            </DropdownMenuItem>
                            {onCopy && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onCopy(asset)}>
                                    <FiCopy className='w-4 h-4' />
                                    Duplicate
                                </DropdownMenuItem>
                            )}
                            {onRecallSettings && asset.generation?.settings && (asset.generation?.model?._id || asset.generation?.model) && (
                                <DropdownMenuItem className='text-xs p-2' onClick={() => onRecallSettings(asset)}>
                                    <IoIosRefresh className='w-3 h-3' />
                                    Recall Settings
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className='text-xs p-2' onClick={() => onAgent(asset)}>
                                <LuBrainCircuit className='w-3 h-3' />
                                Agent
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant='destructive' className='text-xs p-2' onClick={handleOnDelete}>
                                <FiTrash className='w-4 h-4' />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>}

                {showLoader && (
                    <div className='absolute top-0 left-0 w-full h-full bg-black/60 text-white flex items-center justify-center'>
                        <LuLoaderCircle className='w-6 h-6 animate-spin' />
                    </div>
                )}
            </div>
            <div className='flex flex-col items-start px-1 w-full min-w-0'>
                <span className='w-full truncate text-[10px] text-secondary-foreground/50'>{asset.user?.email}</span>
                <AsyncEditableTextArea
                    value={asset.name}
                    onSave={handleRenameSave}
                    loading={savingName}
                    error={nameError}
                    title='Double-click to rename'
                    onEditingChange={setEditingName}
                />
            </div>
        </div>
    )
}
